/**
 * Width-constrained thumbnails for images stored in Supabase Storage.
 *
 * Review media is uploaded at full capture resolution (often 1-2 MB), but most
 * surfaces render it into a small tile. Supabase Storage exposes an image
 * transform endpoint at /storage/v1/render/image/public/... which serves a
 * resized (and, when the browser advertises support, WebP-encoded) variant.
 *
 * Only public Supabase Storage object URLs are rewritten. Anything else
 * (Ticketmaster, Spotify, local assets, data URLs) is returned untouched, so
 * this is safe to apply to any src value.
 *
 * Use this for small cards/tiles only — detail and fullscreen views should keep
 * the original URL so the full-resolution asset is still available.
 */
const STORAGE_OBJECT_PATH = "/storage/v1/object/public/";
const STORAGE_RENDER_PATH = "/storage/v1/render/image/public/";

interface ThumbOptions {
  /** Rendered width in CSS px; the request asks for 2x for retina displays. */
  width: number;
  /** Optional rendered height — when set, the image is cropped to fit. */
  height?: number;
  /** 20-100, defaults to 65 which is visually lossless at tile sizes. */
  quality?: number;
}

export const storageThumbUrl = (
  url: string | undefined,
  { width, height, quality = 65 }: ThumbOptions
): string | undefined => {
  if (!url || !url.includes(STORAGE_OBJECT_PATH)) return url;
  // Already a transform URL — leave it alone.
  if (url.includes(STORAGE_RENDER_PATH)) return url;

  const params = new URLSearchParams();
  params.set("width", String(Math.round(width * 2)));
  if (height) {
    params.set("height", String(Math.round(height * 2)));
    params.set("resize", "cover");
  }
  params.set("quality", String(quality));

  return `${url.replace(STORAGE_OBJECT_PATH, STORAGE_RENDER_PATH)}?${params.toString()}`;
};
